import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { adminAuditApi } from "../../lib/authApi";
import { EmptyLog } from "../../components/illustrations/EmptyIllustrations";
import { EmptyState } from "../../components/ui/empty-state";
import { GridSkeletonRows } from "../../components/ui/grid-skeleton";

const ACTION_LABEL = {
  auth_login: "Login",
  auth_login_failed: "Login fallito",
  auth_logout: "Logout",
  user_create: "Creazione utente",
  user_update: "Modifica utente",
  user_reset_password: "Reset password",
  offer_assign: "Assegnazione offerta",
  offer_status_change: "Cambio stato offerta",
  learning_rule_create: "Regola creata",
  learning_rule_update: "Regola aggiornata",
  learning_rule_disable: "Regola disabilitata",
  learning_rule_delete: "Regola eliminata",
  analyze: "Analisi richiesta",
  patch: "Modifica voci offerta",
  match_voce: "Match singola voce",
  match: "Match completo offerta",
};

const ENTITY_LABEL = {
  auth: "Autenticazione",
  user: "Utente",
  quote_session: "Offerta",
  learning_rule: "Regola insegnamento",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
}

function DiffDisplay({ diff }) {
  if (!diff || Object.keys(diff).length === 0) {
    return (
      <div className="text-[12px] italic" style={{ color: "var(--text-muted)" }}>
        Nessuna modifica registrata
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {Object.entries(diff).map(([k, v]) => {
        if (v && typeof v === "object" && ("before" in v || "after" in v)) {
          return (
            <div key={k} className="text-[12px]">
              <span className="font-semibold">{k}</span>
              <div className="grid grid-cols-2 gap-2 mt-0.5">
                <div
                  className="rounded px-2 py-1 font-mono"
                  style={{
                    background: "#FEF2F2",
                    color: "#991B1B",
                    border: "1px solid #FECACA",
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wide opacity-60">
                    prima
                  </div>
                  {JSON.stringify(v.before) ?? "—"}
                </div>
                <div
                  className="rounded px-2 py-1 font-mono"
                  style={{
                    background: "#F0FDF4",
                    color: "#166534",
                    border: "1px solid #BBF7D0",
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wide opacity-60">
                    dopo
                  </div>
                  {JSON.stringify(v.after) ?? "—"}
                </div>
              </div>
            </div>
          );
        }
        return (
          <div key={k} className="text-[12px] font-mono">
            <span className="font-semibold not-italic">{k}:</span>{" "}
            {JSON.stringify(v)}
          </div>
        );
      })}
    </div>
  );
}

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({
    user_email: "",
    action: "",
    entity_type: "",
    date_from: "",
    date_to: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminAuditApi.list({ ...filters, skip, limit });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Errore caricamento audit log"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  const applyFilters = () => {
    setSkip(0);
    load();
  };

  const resetFilters = () => {
    setFilters({
      user_email: "",
      action: "",
      entity_type: "",
      date_from: "",
      date_to: "",
    });
    setSkip(0);
    setTimeout(load, 0);
  };

  return (
    <div className="p-6 space-y-4 max-w-[1400px] mx-auto" data-testid="admin-audit-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-semibold" style={{ color: "var(--text-primary)" }}>
            Audit log
          </h1>
          <p className="text-[12px] mt-0.5" style={{ color: "var(--text-muted)" }}>
            Tracciato delle azioni degli utenti — chi ha fatto cosa e quando
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded text-[13px]"
          style={{
            background: "var(--bg-surface)",
            border: "1px solid var(--border-default)",
            color: "var(--text-primary)",
          }}
          data-testid="audit-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {/* Filters */}
      <div
        className="rounded p-3 grid grid-cols-1 md:grid-cols-5 gap-2"
        style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <input
          type="text"
          placeholder="Email utente…"
          value={filters.user_email}
          onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="audit-filter-user"
        />
        <input
          type="text"
          placeholder="Azione…"
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="audit-filter-action"
        />
        <select
          value={filters.entity_type}
          onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="audit-filter-entity"
        >
          <option value="">Tutte le entità</option>
          {Object.entries(ENTITY_LABEL).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
        <input
          type="date"
          value={filters.date_from?.slice(0, 10) || ""}
          onChange={(e) =>
            setFilters({
              ...filters,
              date_from: e.target.value ? `${e.target.value}T00:00:00` : "",
            })
          }
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
        />
        <input
          type="date"
          value={filters.date_to?.slice(0, 10) || ""}
          onChange={(e) =>
            setFilters({
              ...filters,
              date_to: e.target.value ? `${e.target.value}T23:59:59` : "",
            })
          }
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
        />
        <div className="md:col-span-5 flex gap-2 justify-end">
          <button
            type="button"
            onClick={resetFilters}
            className="h-8 px-3 text-[12px] rounded"
            style={{
              background: "transparent",
              color: "var(--text-secondary)",
            }}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={applyFilters}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded text-[12px] font-medium"
            style={{
              background: "var(--accent-primary)",
              color: "var(--text-on-accent)",
            }}
            data-testid="audit-apply-btn"
          >
            <Search className="h-3 w-3" />
            Filtra
          </button>
        </div>
      </div>

      {/* Table */}
      <div
        className="rounded overflow-hidden"
        style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <div
          className="grid text-[11px] font-semibold uppercase tracking-wide"
          style={{
            gridTemplateColumns: "180px 220px 180px 1fr 32px",
            background: "var(--bg-subtle)",
            color: "var(--text-muted)",
          }}
        >
          <div className="px-3 py-2.5">Data e ora</div>
          <div className="px-3 py-2.5">Utente</div>
          <div className="px-3 py-2.5">Azione</div>
          <div className="px-3 py-2.5">Sommario</div>
          <div className="px-3 py-2.5"></div>
        </div>
        {loading ? (
          <GridSkeletonRows rows={6} cols={5} />
        ) : items.length === 0 ? (
          <EmptyState
            illustration={EmptyLog}
            title="Nessuna attività registrata"
            subtitle="Le azioni degli utenti compariranno qui."
          />
        ) : (
          items.map((it) => {
            const id = it._id || `${it.ts}-${it.action}`;
            const isOpen = !!expanded[id];
            return (
              <div key={id}>
                <div
                  className="grid items-center text-[12px] cursor-pointer"
                  style={{
                    gridTemplateColumns: "180px 220px 180px 1fr 32px",
                    borderTop: "1px solid var(--border-subtle)",
                  }}
                  onClick={() =>
                    setExpanded((s) => ({ ...s, [id]: !s[id] }))
                  }
                  data-testid={`audit-row-${id}`}
                >
                  <div
                    className="px-3 py-2.5 font-mono text-[11px]"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {fmtDate(it.ts)}
                  </div>
                  <div
                    className="px-3 py-2.5 truncate"
                    style={{ color: "var(--text-primary)" }}
                  >
                    <div className="font-medium">{it.user_email || "—"}</div>
                    {it.user_role && (
                      <div
                        className="text-[10px]"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {it.user_role}
                      </div>
                    )}
                  </div>
                  <div
                    className="px-3 py-2.5 font-mono text-[11px]"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {ACTION_LABEL[it.action] || it.action}
                  </div>
                  <div
                    className="px-3 py-2.5 truncate"
                    style={{ color: "var(--text-primary)" }}
                    title={it.summary}
                  >
                    {it.summary || (
                      <span style={{ color: "var(--text-muted)" }} className="italic">
                        (nessun sommario)
                      </span>
                    )}
                  </div>
                  <div className="px-3 py-2.5" style={{ color: "var(--text-muted)" }}>
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div
                    className="px-3 py-3 space-y-2"
                    style={{
                      background: "var(--bg-subtle)",
                      borderTop: "1px solid var(--border-subtle)",
                    }}
                    data-testid={`audit-row-${id}-detail`}
                  >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[11px]">
                      <div>
                        <span style={{ color: "var(--text-muted)" }}>
                          Entità:
                        </span>{" "}
                        {ENTITY_LABEL[it.entity_type] || it.entity_type}
                      </div>
                      <div className="truncate">
                        <span style={{ color: "var(--text-muted)" }}>ID:</span>{" "}
                        <span className="font-mono">{it.entity_id || "—"}</span>
                      </div>
                      <div>
                        <span style={{ color: "var(--text-muted)" }}>IP:</span>{" "}
                        <span className="font-mono">{it.ip || "—"}</span>
                      </div>
                      <div>
                        <span style={{ color: "var(--text-muted)" }}>
                          User ID:
                        </span>{" "}
                        <span className="font-mono">{it.user_id || "—"}</span>
                      </div>
                    </div>
                    <DiffDisplay diff={it.diff || {}} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      <div
        className="flex items-center justify-between text-[12px]"
        style={{ color: "var(--text-muted)" }}
      >
        <div>
          {total > 0
            ? `Voci ${skip + 1}–${Math.min(skip + limit, total)} di ${total}`
            : "0 voci"}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0 || loading}
            className="h-8 px-3 rounded text-[12px] disabled:opacity-40"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-default)",
              color: "var(--text-primary)",
            }}
          >
            Precedente
          </button>
          <button
            type="button"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total || loading}
            className="h-8 px-3 rounded text-[12px] disabled:opacity-40"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-default)",
              color: "var(--text-primary)",
            }}
          >
            Successiva
          </button>
        </div>
      </div>
    </div>
  );
}
